import React, { useState, useEffect, useContext, useId } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Container,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapLocationDot } from "@fortawesome/free-solid-svg-icons";

import { DataContext } from "../../../context/DataContext";
import { getRuta } from "../../../helpers/Helpers";
import { tienePermiso } from "../../../helpers/permisos";
import { getToken } from "../../../api/Account";
import {
  addCommunityAsync,
  deleteCommunityAsync,
  getMunicipalityByIdAsync,
  updateCommunityAsync,
} from "../../../api/Locations";
import {
  CargandoTabla,
  EstadoError,
  EstadoVacio,
  PageHeader,
  useAviso,
} from "../../../components/base";
import {
  DialogoFormulario,
  PAGINACION_ARIA,
  encabezadoTabla,
  normalizar,
  useCargaLocal,
  useConfirmacion,
  useEnvio,
  useEscritura,
} from "../comunes";

const FILAS_POR_PAGINA = 10;

/**
 * Alta (sin `comunidad`) o cambio de nombre de una comunidad. El nombre es obligatorio y, al
 * editar, tiene que ser distinto del actual.
 */
const DialogoComunidad = ({ comunidad, idMunicipio, nombreMunicipio, onCerrar, onGuardado }) => {
  const token = getToken();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();
  const id = useId();
  const esAlta = !comunidad;
  const original = comunidad?.name ?? "";
  const [nombre, setNombre] = useState(original);
  const [intentado, setIntentado] = useState(false);

  const limpio = nombre.trim();
  let errorCampo = "";
  if (intentado && limpio === "") errorCampo = "Escriba el nombre de la comunidad";
  else if (intentado && !esAlta && limpio === original.trim()) errorCampo = "Escriba un nombre distinto del actual";

  const guardar = () =>
    enviar(async () => {
      setIntentado(true);
      if (limpio === "" || (!esAlta && limpio === original.trim())) return;
      const ok = await escribir(
        () =>
          esAlta
            ? addCommunityAsync(token, { idMunicipality: idMunicipio, name: limpio })
            : updateCommunityAsync(token, { id: comunidad.id, name: limpio }),
        esAlta ? "No se pudo agregar la comunidad" : "No se pudo cambiar el nombre"
      );
      if (ok) onGuardado(limpio);
    });

  return (
    <DialogoFormulario
      titulo={esAlta ? `Nueva comunidad en ${nombreMunicipio}` : "Cambiar nombre de la comunidad"}
      onCerrar={onCerrar}
      hayCambios={limpio !== original.trim()}
      guardando={guardando}
      onEnviar={guardar}
      textoEnviar={esAlta ? "Agregar comunidad" : "Guardar nombre"}
    >
      <TextField
        fullWidth
        autoFocus
        required
        id={`${id}-nombre`}
        label="Nombre de la comunidad"
        value={nombre}
        onChange={(e) => setNombre(e.target.value.toUpperCase())}
        error={Boolean(errorCampo)}
        helperText={errorCampo || " "}
        disabled={guardando}
        inputProps={{ autoComplete: "off" }}
        sx={{ mt: 1 }}
      />
    </DialogoFormulario>
  );
};

export const Communities = () => {
  let ruta = getRuta();
  const token = getToken();
  let navigate = useNavigate();
  const { id } = useParams();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir: pedirConfirmacion, dialogo: confirmacion } = useConfirmacion();

  const [municipality, setMunicipality] = useState(null);
  const [recargar, setRecargar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagina, setPagina] = useState(0);
  const [menu, setMenu] = useState({ ancla: null, fila: null });
  // null: cerrado; { comunidad: null } alta; { comunidad } edición.
  const [dialogo, setDialogo] = useState(null);

  const puedeAgregar = tienePermiso(access, "CONFIG CREATE");
  const puedeEditar = tienePermiso(access, "CONFIG UPDATE");
  const puedeBorrar = tienePermiso(access, "CONFIG DELETE");

  const { cargando, error, cargar } = useCargaLocal(() => getMunicipalityByIdAsync(token, id));

  useEffect(() => {
    let vigente = true;
    (async () => {
      const result = await cargar();
      if (vigente && result) setMunicipality(result.data.result);
    })();
    return () => {
      vigente = false;
    };
  }, [recargar, id]);

  const comunidades = [...(municipality?.communities ?? [])].sort((a, b) =>
    String(a.name ?? "").localeCompare(String(b.name ?? ""))
  );
  const termino = normalizar(searchTerm.trim());
  const withSearch = comunidades.filter(
    (val) => termino === "" || normalizar(val.name).includes(termino)
  );

  useEffect(() => {
    if (pagina > 0 && pagina * FILAS_POR_PAGINA >= withSearch.length) setPagina(0);
  }, [pagina, withSearch.length]);

  const currentItem = withSearch.slice(pagina * FILAS_POR_PAGINA, (pagina + 1) * FILAS_POR_PAGINA);

  const onChangeSearch = (val) => {
    setPagina(0);
    setSearchTerm(val);
  };

  const recargarLista = () => setRecargar((actual) => !actual);

  const borrar = async (fila) => {
    // El servidor no deja borrar una comunidad donde viven clientes y lo explica en el aviso.
    const ok = await escribir(() => deleteCommunityAsync(token, fila.id), "No se pudo borrar la comunidad");
    if (!ok) return;
    recargarLista();
    aviso.exito(`Comunidad ${fila.name} borrada`);
  };

  const pedirBorrar = (fila) =>
    pedirConfirmacion({
      titulo: "Borrar comunidad",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se borrará la comunidad ${fila.name} de ${municipality?.name ?? "este municipio"}.`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Si hay clientes que viven en ella no se podrá borrar: primero cámbielos de comunidad.
          </Typography>
        </>
      ),
      textoConfirmar: "Borrar comunidad",
      peligro: true,
      onConfirmar: () => borrar(fila),
    });

  const botonAgregar = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogo({ comunidad: null })}>
      Agregar comunidad
    </Button>
  );

  const hayAcciones = puedeEditar || puedeBorrar;

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={6} columnas={2} etiqueta="Cargando comunidades…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar las comunidades"
        }
        onReintentar={recargarLista}
      />
    );
  } else if (withSearch.length === 0) {
    contenido = termino ? (
      <EstadoVacio
        titulo="Sin resultados"
        descripcion={`Ninguna comunidad coincide con «${searchTerm.trim()}».`}
        accion={
          <Button variant="outlined" onClick={() => onChangeSearch("")}>
            Borrar búsqueda
          </Button>
        }
      />
    ) : (
      <EstadoVacio
        titulo="Este municipio no tiene comunidades"
        descripcion="Las comunidades son los barrios, comarcas o caseríos que se eligen en la dirección del cliente."
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table aria-label={`Comunidades de ${municipality.name}`}>
            <TableHead>
              <TableRow>
                <TableCell sx={encabezadoTabla}>Comunidad</TableCell>
                {hayAcciones ? (
                  <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>Acciones</TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentItem.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  {hayAcciones ? (
                    <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {puedeEditar ? (
                        <Tooltip title="Cambiar nombre">
                          <IconButton
                            aria-label={`Cambiar el nombre de ${row.name}`}
                            onClick={() => setDialogo({ comunidad: row })}
                            sx={{ color: "primary.main" }}
                          >
                            <EditOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {puedeBorrar ? (
                        <Tooltip title="Más acciones">
                          <IconButton
                            aria-label={`Más acciones de ${row.name}`}
                            aria-haspopup="menu"
                            onClick={(e) => setMenu({ ancla: e.currentTarget, fila: row })}
                            sx={{ color: "text.secondary" }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={withSearch.length}
          page={pagina}
          onPageChange={(e, nueva) => setPagina(nueva)}
          rowsPerPage={FILAS_POR_PAGINA}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          getItemAriaLabel={PAGINACION_ARIA}
        />
      </>
    );
  }

  const departamento = municipality?.department;

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo={municipality ? `Comunidades de ${municipality.name}` : "Comunidades"}
        subtitulo={departamento ? `Departamento de ${departamento.name}` : undefined}
        icono={<FontAwesomeIcon icon={faMapLocationDot} />}
        acciones={
          <>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() =>
                navigate(departamento ? `${ruta}/department/${departamento.id}` : `${ruta}/locations`)
              }
            >
              Municipios
            </Button>
            {puedeAgregar && municipality ? botonAgregar : null}
          </>
        }
      />

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <TextField
          size="small"
          type="search"
          onChange={(e) => onChangeSearch(e.target.value)}
          value={searchTerm}
          placeholder="Nombre de la comunidad"
          inputProps={{ "aria-label": "Buscar comunidad", autoComplete: "off" }}
          sx={{ width: { xs: "100%", md: 380 }, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Borrar búsqueda" onClick={() => onChangeSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {contenido}
      </Paper>

      <Menu
        anchorEl={menu.ancla}
        open={Boolean(menu.ancla)}
        onClose={() => setMenu((actual) => ({ ...actual, ancla: null }))}
      >
        <MenuItem
          onClick={() => {
            const fila = menu.fila;
            setMenu((actual) => ({ ...actual, ancla: null }));
            pedirBorrar(fila);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <DeleteOutlineIcon />
          </ListItemIcon>
          <ListItemText>Borrar comunidad</ListItemText>
        </MenuItem>
      </Menu>

      {dialogo ? (
        <DialogoComunidad
          comunidad={dialogo.comunidad}
          idMunicipio={id}
          nombreMunicipio={municipality?.name ?? ""}
          onCerrar={() => setDialogo(null)}
          onGuardado={(nombre) => {
            aviso.exito(dialogo.comunidad ? `Comunidad renombrada: ${nombre}` : `Comunidad ${nombre} agregada`);
            setDialogo(null);
            recargarLista();
          }}
        />
      ) : null}

      {confirmacion}
    </Container>
  );
};

export default Communities;
